import { useMemo, useState } from "react";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { searchAccounts, type PickableAccount } from "@/lib/unifiedAccounts";
import { cn } from "@/lib/utils";

const KIND_LABEL = { proveedor: "Proveedor", cliente: "Cliente", general: "General" } as const;

interface Props {
  value: string;
  accounts: PickableAccount[];
  onChange: (account: string) => void;
}

/** Casilla de contrapartida: se escribe cuenta, razón social o NIF y se elige del catálogo. */
const AccountPicker = ({ value, accounts, onChange }: Props) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState<string | null>(null);
  const [hi, setHi] = useState(0);
  const results = useMemo(() => (open ? searchAccounts(accounts, query ?? "", 40) : []), [open, accounts, query]);

  const pick = (acc: string) => { onChange(acc); setQuery(null); setOpen(false); };
  const commitTyped = () => {
    if (query === null) return;
    const digits = query.replace(/\s/g, "");
    if (/^\d{1,12}$/.test(digits)) onChange(digits);
    setQuery(null);
  };

  return (
    <Popover open={open && results.length > 0} onOpenChange={(o) => { if (!o) { commitTyped(); setOpen(false); } }}>
      <PopoverAnchor asChild>
        <Input
          value={query ?? value}
          onFocus={(e) => { setOpen(true); setHi(0); e.currentTarget.select(); }}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); setHi(0); }}
          onBlur={() => setTimeout(() => { setOpen(false); commitTyped(); }, 150)}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, results.length - 1)); }
            else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
            else if (e.key === "Enter") { e.preventDefault(); if (results[hi]) pick(results[hi].account); else { commitTyped(); setOpen(false); } }
            else if (e.key === "Escape") { setQuery(null); setOpen(false); }
          }}
          placeholder="Cuenta, nombre o NIF"
          aria-label="Cuenta contrapartida"
          className="h-8 font-mono text-xs"
        />
      </PopoverAnchor>
      <PopoverContent
        align="start"
        className="w-96 max-w-[90vw] p-1 max-h-72 overflow-y-auto"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        {results.map((a, i) => (
          <button
            key={`${a.kind}-${a.account}`}
            type="button"
            onMouseDown={(e) => { e.preventDefault(); pick(a.account); }}
            className={cn("w-full text-left rounded px-2 py-1.5 text-xs flex items-start gap-2", i === hi ? "bg-accent text-accent-foreground" : "")}
          >
            <span className="font-mono shrink-0">{a.account}</span>
            <span className="flex-1 min-w-0">
              <span className="block truncate">{a.name}</span>
              {a.taxId ? <span className="block text-muted-foreground">{a.taxId}</span> : null}
            </span>
            <span className="text-muted-foreground shrink-0">{KIND_LABEL[a.kind]}</span>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
};

export default AccountPicker;
